import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import Header from '#/components/Form/Header';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import { TRootState } from '#/redux/store';
import ApiManager from '#/services/api/ApiManager';
import { header2ClassName, spotifyApiConfig } from '#/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import tinyColor from 'tinycolor2';
import Vibrant from 'node-vibrant';
import useProfile from '#/hooks/useProfile';
import TrackListItem from '#/components/Layout/TrackListItem/TrackListItem';
import PreviewLayout from '#/components/Layout/PreviewLayout/PreviewLayout';
import { getItemsPerBreakpoint } from '#/utils/getItemsPerBreakpoint';
import useViewport from '#/hooks/useViewport';
import ArtistPreview from '#/components/Layout/ArtistPreview/ArtistPreview';
import { ROUTE_NAMES } from '#/router/routes';
import {
  createActionUserProfileAddTopArtists,
  createActionUserProfileAddTopTracks,
  createActionUserProfileErrorTopArtists,
  createActionUserProfileErrorTopTracks,
  createActionUserProfileLoadingTopArtists,
  createActionUserProfileLoadingTopTracks,
  createActionUserProfileSetTopTrackIsFavorite,
} from '#/redux/actions/userProfile';
import { ITopArtistsResponse } from '#/types/API/topArtists';
import { ITopTracksResponse } from '#/types/API/topTracks';
import PlaylistPreview from '#/components/Layout/PlaylistPreview/PlaylistPreview';
import { convert } from 'html-to-text';
import {
  emptyContainsResponse,
  urisParameter,
} from '#/utils/spotifyApiHelpers';

const userProfileSelector = (state: TRootState) => state.userProfile;

const librarySelector = (state: TRootState) => state.library;

type TColor = [number, number, number];

const UserProfile = () => {
  const imageReference = useRef<HTMLImageElement>(null);

  const profile = useProfile();

  const { viewportWidth } = useViewport();

  const navigate = useNavigate();

  const [dominantColor, setDominantColor] = useState<undefined | TColor>([
    77, 77, 77,
  ]);

  const userProfileSlice = useSelector(userProfileSelector);

  const { topArtists, topTracks } = userProfileSlice;

  const librarySlice = useSelector(librarySelector);

  const { playlists, artists } = librarySlice;

  const dispatch = useDispatch();

  const protectedSpotify = useProtectedSpotify();

  const topArtistLimit = 6;

  const topTracksLimit = 6;

  useEffect(() => {
    const getTopArtists = async () => {
      dispatch(createActionUserProfileLoadingTopArtists());
      const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

      const response = await apiManager.getAllEntities<ITopArtistsResponse>(
        'topArtists',
        { limit: topArtistLimit }
      );

      if ('error' in response) {
        // TODO display error notification
        dispatch(createActionUserProfileErrorTopArtists());
      } else {
        dispatch(createActionUserProfileAddTopArtists(response.data.items));
      }
    };

    const getTopTracks = async () => {
      dispatch(createActionUserProfileLoadingTopTracks());
      const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

      const response = await apiManager.getAllEntities<ITopTracksResponse>(
        'topTracks',
        { limit: topTracksLimit }
      );

      if ('error' in response) {
        // TODO display error notification
        dispatch(createActionUserProfileErrorTopTracks());
      } else {
        const topTrackIds = response.data.items.map((track) => track.id);
        const responseFavTracks =
          topTrackIds.length > 0
            ? await apiManager.getAllEntities<boolean[]>('libraryContains', {
                uris: urisParameter('track', topTrackIds),
              })
            : await emptyContainsResponse();
        if ('error' in responseFavTracks) {
          // TODO display error notification
          dispatch(createActionUserProfileErrorTopTracks());
        } else {
          const tracksWithFavorite = response.data.items.map((track, index) => {
            const trackWithFavorite = {
              ...track,
              favorite: responseFavTracks.data[index],
            };
            return trackWithFavorite;
          });
          dispatch(createActionUserProfileAddTopTracks(tracksWithFavorite));
        }
      }
    };

    getTopArtists();

    getTopTracks();
  }, [dispatch, protectedSpotify]);

  useEffect(() => {
    if (profile && profile.images.length > 0) {
      Vibrant.from(profile.images[0].url)
        .getPalette()
        .then((palette) => setDominantColor(palette.Vibrant?.getRgb()));
    }
  }, [profile]);

  const getBannerStyle = () => {
    const style: HTMLAttributes<HTMLDivElement>['style'] = {};
    if (dominantColor) {
      const color = tinyColor(`rgb(${dominantColor.join(', ')})`);
      const darkerDominant = tinyColor(
        `rgb(${dominantColor.join(', ')})`
      ).darken(22);
      style.backgroundImage = `linear-gradient(${color.toRgbString()}, ${darkerDominant.toRgbString()})`;
    }
    return style;
  };

  const getBodyStyle = () => {
    const style: HTMLAttributes<HTMLDivElement>['style'] = {};
    if (dominantColor) {
      const darkerDominant = tinyColor(
        `rgb(${dominantColor.join(', ')})`
      ).darken(28);
      style.backgroundImage = `linear-gradient(${darkerDominant.toRgbString()}, transparent 240px)`;
    }
    return style;
  };

  return (
    <div className="mt-theme-top-bar-negative-height">
      <div className="pt-theme-top-bar-height" style={getBannerStyle()}>
        <div className="flex gap-6 pt-9 p-theme-page-padding">
          {profile && profile.images.length > 0 && (
            <div className=" w-[192px] 2xl:w-[232px]">
              <div className="w-full aspect-square overflow-hidden rounded-full shadow-2xl shadow-theme-shadow bg-theme-dark-gray">
                <img
                  className="  h-full w-full object-cover"
                  ref={imageReference}
                  src={profile?.images[0].url}
                  alt=""
                />
              </div>
            </div>
          )}
          <div className=" flex-1 flex flex-col justify-end pb-1">
            <span className="font-bold text-sm">Profile</span>
            <Header
              type="h1"
              className="text-[6rem] leading-[6rem] mb-9 font-bold tracking-tighter"
            >
              {profile?.display_name}
            </Header>
            <span>{`${
              Array.isArray(playlists) ? playlists.length : '0'
            } Public Playlists • ${
              Array.isArray(artists) ? artists.length : '0'
            } Following`}</span>
          </div>
        </div>
      </div>
      <div className="min-h-full p-theme-page-padding" style={getBodyStyle()}>
        <div className=" mb-10">
          <Header type="h2" className={header2ClassName}>
            Your top tracks
          </Header>
          <div>
            {Array.isArray(topTracks) &&
              topTracks?.map((track, index) => {
                const handleClickFavoriteTrack = async () => {
                  const apiMannager = new ApiManager(
                    spotifyApiConfig,
                    protectedSpotify
                  );
                  if (track.favorite) {
                    const responseRemoveFromFavorites =
                      await apiMannager.deleteEntity('library', '', {
                        uris: urisParameter('track', track.id),
                      });

                    if ('error' in responseRemoveFromFavorites) {
                      //TODO: handle error
                    } else {
                      dispatch(
                        createActionUserProfileSetTopTrackIsFavorite({
                          track: track.id,
                          favorite: false,
                        })
                      );
                    }
                  } else {
                    const responseAddToFavorites =
                      await apiMannager.replaceEntity(
                        'library',
                        '',
                        undefined,
                        {
                          uris: urisParameter('track', track.id),
                        }
                      );

                    if ('error' in responseAddToFavorites) {
                      //TODO: handle error
                    } else {
                      //
                      dispatch(
                        createActionUserProfileSetTopTrackIsFavorite({
                          track: track.id,
                          favorite: true,
                        })
                      );
                    }
                  }
                };

                return index <= 4 ? (
                  <TrackListItem
                    id={track.id}
                    onFavorite={handleClickFavoriteTrack}
                    key={track.id}
                    explicit={track.explicit}
                    image={track.album.images[0].url}
                    name={track.name}
                    duration={track.duration_ms}
                    number={index + 1}
                    followed={track.favorite}
                    uri={track.uri}
                    contextUri={track.album.uri}
                  />
                ) : undefined;
              })}
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center">
            <Header type="h2" className={header2ClassName}>
              Your top artists
            </Header>
          </div>
          <PreviewLayout>
            {Array.isArray(topArtists) &&
              topArtists?.map((artist, index) => {
                if (index >= getItemsPerBreakpoint(viewportWidth)) {
                  return;
                }
                const { id, name, images, uri } = artist;
                const handleOnClick = () =>
                  navigate(`${ROUTE_NAMES.ARTIST}/${id}`);
                return (
                  <ArtistPreview
                    uri={uri}
                    key={id}
                    onClick={handleOnClick}
                    name={name}
                    image={images[0].url}
                  />
                );
              })}
          </PreviewLayout>
        </div>
        <div>
          <Header type="h2" className={header2ClassName}>
            Your playlists
          </Header>
          <PreviewLayout>
            {Array.isArray(playlists) &&
              playlists.map((featuredPlaylist, index) => {
                if (index >= getItemsPerBreakpoint(viewportWidth, 2)) {
                  return;
                }

                const handleOnClick = () =>
                  navigate(`${ROUTE_NAMES.PLAYLIST}/${featuredPlaylist.id}`);

                return (
                  <PlaylistPreview
                    key={featuredPlaylist.id}
                    image={featuredPlaylist.images[0].url}
                    name={featuredPlaylist.name}
                    description={convert(featuredPlaylist.description, {
                      wordwrap: false,
                    })}
                    onClick={handleOnClick}
                    uri={featuredPlaylist.uri}
                  />
                );
              })}
          </PreviewLayout>
        </div>
        <div>
          <div className="flex justify-between items-center">
            <Header type="h2" className={header2ClassName}>
              Following
            </Header>
            <Link className="link" to={ROUTE_NAMES.PROFILE_FOLLOWING}>
              Show all
            </Link>
          </div>
          <PreviewLayout>
            {Array.isArray(artists) &&
              artists?.map((artist, index) => {
                if (index >= getItemsPerBreakpoint(viewportWidth)) {
                  return;
                }
                const { id, name, images, uri } = artist;
                const handleOnClick = () =>
                  navigate(`${ROUTE_NAMES.ARTIST}/${id}`);
                return (
                  <ArtistPreview
                    uri={uri}
                    key={id}
                    onClick={handleOnClick}
                    name={name}
                    image={images[0].url}
                  />
                );
              })}
          </PreviewLayout>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
